<script setup lang="ts">
//#region imports
import { cloneDeep } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { useActivitiesLanguage } from '@/views/CRM/composables/useActivitiesLanguage';
import { useSmsStat } from '@/views/CRM/composables/useSmsStat';
import LanguagesSetup from '@/components/UIElements/LanguagesSetup.vue';
import { useTypedStore } from '@/store';
import type { EmitsToListeners } from '@/types/core';
import type { CrmActivity, CrmActionGroup, CrmAction } from '@/types/crm';
//#endregion

//#region types
interface Props {
  activity?: CrmActivity;
  group: CrmActionGroup;
  payload: any;
}

interface Emits {
  'send-test-sms': [{ actionGroup: CrmActionGroup, index: number }];
  save: [actionGroup: CrmActionGroup | null, actions: CrmAction[], actionType: 'sms'];
  close: [];
}

export interface AddSmsActionParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  activity: () => ({ action_groups: [] } as unknown as CrmActivity),
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const store = useTypedStore();
//#endregion

//#region composables
const isVisible = ref(false);
const name = ref('');
const message = ref('');
const index = ref(-1);
const actionGroupIndex = ref(-1);
const actionGroup = ref<CrmActionGroup | null>(null);
const actions = ref<CrmAction[]>([]);
const errors = ref<any[]>([]);

const languagesFromStore = computed(() => store.state.misc?.languages || []);

const {
  languages,
  selectedLanguages,
  defaultLanguageActionIndex,
  handleSelectTab,
  handleMakeDefault,
  setActionsValue,
} = useActivitiesLanguage<CrmAction>({
  actions,
  index,
  actionGroupIndex,
  languagesFromStore,
});

const { messageStats, messageStyle } = useSmsStat({ message });
//#endregion

//#region computed
const currentErrors = computed(() => errors.value[index.value] || {});
const isValidSendTest = computed(() => !!message.value);
//#endregion

//#region methods
function getDefaultForm(): CrmAction {
  return {
    name: '',
    type: 'sms',
    message: '',
    language_code: 'en',
    is_default: true,
  } as unknown as CrmAction;
}

function updateActionValue(actionIndex: number) {
  message.value = actions.value[actionIndex]?.message || '';
  name.value = actions.value[actionIndex]?.name || '';
}

function open() {
  const group = props.group;
  const payload = props.payload;

  actionGroup.value = group;
  actionGroupIndex.value = props.activity.action_groups.indexOf(group);

  const sourceActions = (props.activity.action_groups[actionGroupIndex.value]?.actions || [])
    .filter((item) => item.type === 'sms');

  if (!sourceActions.length) {
    sourceActions.push(getDefaultForm());
  }

  actions.value = cloneDeep(sourceActions);
  index.value = 0;
  isVisible.value = true;
  errors.value = [];

  if (payload?.errors?.sms) {
    errors.value = actions.value.map((_, actionIndex) => payload.errors?.sms?.[actionIndex] || null);
  }
}

function close() {
  isVisible.value = false;
  emit('close');
}

function handleSave() {
  emit('save', actionGroup.value, actions.value, 'sms');
  close();
}

function handleSendTestSms() {
  emit('send-test-sms', {
    actionGroup: {
      ...(actionGroup.value || {}),
      actions: actions.value,
    } as CrmActionGroup,
    index: index.value,
  });
}

function handleAddLanguage(languageCode: string) {
  actions.value.push({
    ...getDefaultForm(),
    language_code: languageCode,
    is_default: false,
  });
}

function handleDelete() {
  actions.value.splice(index.value, 1);
  errors.value.splice(index.value, 1);

  index.value = defaultLanguageActionIndex.value;
  updateActionValue(index.value);
}
//#endregion

//#region watch
watch(index, (value) => {
  if (value !== -1) {
    updateActionValue(value);
  }
}, { immediate: true });

watch(message, (value) => {
  setActionsValue('message', value);
});

watch(name, (value) => {
  setActionsValue('name', value);
});
//#endregion

//#region hooks
defineExpose({
  open,
  close,
});
//#endregion
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="$t('crm.activities.actions.action_popup.sms_title')"
    :width="800"
    @close="close()"
  >
    <template v-slot:body>
      <div class="t:space-y-16 t:-mb-16">
        <UiInput
          v-model="name"
          :label="$t('crm.activities.actions.action_popup.sms_fields.name')"
          autosize
          size="big"
          :placeholder="$t('crm.activities.actions.action_popup.sms_fields.name')"
          type="text"
          :error="currentErrors.name"
        />
        <LanguagesSetup
          class="t:-mx-24"
          :selected-index="index"
          :languages="languages"
          :selected-languages="selectedLanguages"
          :errors="errors"
          @add-language="handleAddLanguage"
          @select-index="handleSelectTab"
          @delete-language="handleDelete"
          @make-default="handleMakeDefault(index)"
        >
          <div class="t:space-y-8">
            <UiTextarea
              v-model="message"
              :placeholder="$t('crm.activities.actions.action_popup.sms_fields.message')"
              :rows="6"
              :error="currentErrors.message"
            />
            <UiHelp
              :text="messageStats"
              :style="messageStyle"
            />
          </div>
        </LanguagesSetup>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          @click="close()"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          icon="paper-plane"
          :disabled="!isValidSendTest"
          @click="handleSendTestSms()"
        >
          {{ $t('crm.buttons.send_test_sms') }}
        </UiButton>
        <UiButton
          icon="check"
          filled
          @click="handleSave()"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
